/**
 * <share-action>
 *
 * Wraps a single button that triggers a share flow. Uses the Web Share API
 * when available (mobile + Safari + Chromium-on-Android) and falls back to
 * `navigator.clipboard.writeText` with a small in-place toast confirmation.
 *
 * Markup contract:
 *   <share-action
 *     data-share-url="https://…/products/foo"
 *     data-share-title="Mara - Kralen armband"
 *     data-share-text="Gespot bij Mae 💛"
 *     data-copied-label="Link gekopieerd"
 *   >
 *     <button type="button">…</button>
 *     <span class="share-action__toast" hidden></span>
 *   </share-action>
 */
class ShareAction extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;
  /** @type {HTMLElement | null} */
  #toast = null;
  /** @type {number | undefined} */
  #toastTimer;

  connectedCallback() {
    this.#button = this.querySelector('button');
    this.#toast = this.querySelector('.share-action__toast');
    this.#button?.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#handleClick);
    if (this.#toastTimer) clearTimeout(this.#toastTimer);
  }

  #handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const url = this.dataset.shareUrl || window.location.href;
    const title = this.dataset.shareTitle || document.title;
    const text = this.dataset.shareText || '';

    // Prefer the native share sheet — gives users WhatsApp/Mail/Messages in one tap.
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (error) {
        // AbortError = user dismissed the share sheet; no toast needed.
        if (error && error.name === 'AbortError') return;
        // Anything else: fall through to clipboard fallback.
      }
    }

    // Clipboard fallback.
    try {
      await navigator.clipboard.writeText(url);
      this.#showToast(this.dataset.copiedLabel || 'Link copied');
    } catch (_error) {
      // Final fallback if clipboard is denied (e.g., insecure context).
      window.prompt(this.dataset.copiedLabel || 'Link', url);
    }
  };

  /** @param {string} message */
  #showToast(message) {
    if (!this.#toast) return;
    this.#toast.textContent = message;
    this.#toast.hidden = false;
    this.#toast.classList.add('is-visible');

    if (this.#toastTimer) clearTimeout(this.#toastTimer);
    this.#toastTimer = window.setTimeout(() => {
      if (!this.#toast) return;
      this.#toast.classList.remove('is-visible');
      // Keep DOM around for screen readers a bit longer before hiding.
      this.#toastTimer = window.setTimeout(() => {
        if (this.#toast) this.#toast.hidden = true;
      }, 250);
    }, 1800);
  }
}

if (!customElements.get('share-action')) {
  customElements.define('share-action', ShareAction);
}
